import { useEffect, useState } from 'react';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Col, Grid, Row } from 'antd';

import Advertising from 'src/components/Home/Article/Advertising';
import CardArticle from 'src/components/Home/Article/CardArticle';
import MainArticle from 'src/components/Home/Article/MainArticle';
import ImageC from 'src/components/Image';
import SpinC from 'src/components/Layout/Spin';
import { Title } from 'src/components/Layout/Typography';
import g from 'src/constants/global';
import { DEFAULT_PAGE_INDEX, DEFAULT_PER_PAGE } from 'src/constants/pagination';
import useInsiderTrackingCategory from 'src/hooks/useInsiderTrackingCategory';
import { IArticleByGroupDetail, ICategoryByGroupDetail } from 'src/interface';
import { TargetArrayType } from 'src/interface/AdSlot/index';
import { actionGetArticleByCategorySlug } from 'src/redux/actions/articleByGroup';
import deviceSizeC from 'src/utils/device-size-client';
import { truncateString } from 'src/utils/func';

import classes from './style.module.css';

interface SubCategoryMenuProps {
	adSource: string;
	adTarget: TargetArrayType;
}

const GPT = dynamic(() => import('src/components/GPT'), { ssr: false });
const SubCategoryMenu = (props: SubCategoryMenuProps) => {
	const breakpoint = Grid.useBreakpoint();
	const dispatch = useDispatch();
	const router = useRouter();
	const { homepage, categoryByGroup, articleByGroup, loader } = useSelector(
		(state: RootStateOrAny) => state,
	);

	const subCategories: ICategoryByGroupDetail[] = categoryByGroup.subCategories || [];
	const articlesResponse = articleByGroup.articleByCategory || {};
	const [articlesPublic, setArticlesPublic] = useState<IArticleByGroupDetail[]>(
		articlesResponse?.data?.records,
	);
	const totalPage: number = articlesResponse?.meta?.pagination?.total_page || 0;

	const dataAds = homepage.banner?.data?.record?.ads?.ads || [];

	const [pagination, setPagination] = useState({
		page: DEFAULT_PAGE_INDEX,
		per_page: DEFAULT_PER_PAGE,
	});

	useInsiderTrackingCategory('articles', articlesPublic);

	const getArticles = async (slug: string) => {
		dispatch(
			await actionGetArticleByCategorySlug(slug, {
				should_show_headline: true,
				...pagination,
			}),
		);
	};

	useEffect(() => {
		if (router.pathname) getArticles(router.pathname.split('/').splice(-1)[0]);
	}, [router.pathname, pagination]);

	useEffect(() => {
		if (articlesResponse?.meta?.pagination?.page > 1 && articlesResponse?.data?.records?.length) {
			setArticlesPublic([...articlesPublic, ...articlesResponse?.data?.records]);
		}
	}, [articlesResponse?.meta?.pagination?.page]);

	return (
		<>
			{deviceSizeC(breakpoint) === 'desktop' && (
				<GPT
					name="gpt-ad-article-desktop-banner"
					adUnit={g.AD.CTG_ARTICLE_LLEADERBOARD_UNIT}
					size={g.AD.CTG_ARTICLE_LLEADERBOARD_SIZE}
					target={props.adTarget}
					style={{ margin: '-18px 0 16px' }}
				/>
			)}
			{deviceSizeC(breakpoint) === 'mobile' && (
				<GPT
					name="gpt-ad-landing-category-general-halfbanner"
					adUnit={g.AD.CTG_ARTICLE_MOBILE_HALFBANNER_UNIT}
					size={g.AD.CTG_ARTICLE_MOBILE_HALFBANNER_SIZE}
					target={props.adTarget}
					style={{ margin: '-4px 0 14px' }}
				/>
			)}
			<ul className='flex space-x-3 my-2 md:space-x-4 md:my-3 overflow-x-scroll scrollbar-none whitespace-nowrap'>
				{subCategories.map((sc: ICategoryByGroupDetail) => (
					<li key={sc.uid}>
						<Link href={sc.full_slug}>
							<Title level={2} text={truncateString(sc.name, 25)} className="fs-16" />
						</Link>
					</li>
				))}

				<li>
					<Link href="semua-kategori">
						<Title level={2} text="Semua Kategori" className="fs-16" />
					</Link>
				</li>
			</ul>
			<Row gutter={[24, 8]}>
				<Col xs={24} sm={24} md={24} lg={16} xl={16}>
					<MainArticle data={articlesPublic[0]} />

					<div className={classes.section}>
						<CardArticle data={articlesPublic} length={articlesPublic.length} />
					</div>

					{pagination.page < totalPage && (
						<div
							className={classes.viewMore}
							onClick={() =>
								setPagination((prev) => ({
									...prev,
									page: prev.page + 1,
									per_page: 10,
								}))
							}
						>
							{loader?.sending ? (
								<SpinC />
							) : (
								<>
									<p>Muat lebih banyak</p>
									<ImageC
										alt="arrow-down-short"
										src="/icons/arrow-down-short.svg"
										height={breakpoint.xs ? 24 : 32}
										width={breakpoint.xs ? 24 : 32}
									/>
								</>
							)}
						</div>
					)}
				</Col>

				{breakpoint.lg && (
					<Col span={8}>
						<Advertising
							ads={dataAds}
							adSource={props.adSource}
							adTarget={props.adTarget}
							adUnits={[g.AD.CTG_ARTICLE_HALFPAGE_UNIT, g.AD.CTG_ARTICLE_MEDIUMREC_UNIT]}
							sizes={[g.AD.CTG_ARTICLE_HALFPAGE_SIZE, g.AD.CTG_ARTICLE_MEDIUMREC_SIZE]}
						/>
					</Col>
				)}
			</Row>
		</>
	);
};

export default SubCategoryMenu;
